import { AdminUser } from '../../types/user';

/**
 * Authentication service for managing tokens and user session
 */

const TOKEN_KEY = 'admin_token';
const USER_KEY = 'admin_user';

/**
 * Store authentication token in localStorage
 */
export const setToken = (token: string): void => {
  localStorage.setItem(TOKEN_KEY, token);
};

/**
 * Retrieve authentication token from localStorage
 */
export const getToken = (): string | null => {
  return localStorage.getItem(TOKEN_KEY);
};

/**
 * Remove authentication token from localStorage
 */
export const removeToken = (): void => {
  localStorage.removeItem(TOKEN_KEY);
};

/**
 * Store user data in localStorage
 */
export const setUser = (user: AdminUser): void => {
  localStorage.setItem(USER_KEY, JSON.stringify(user));
};

/**
 * Retrieve user data from localStorage
 */
export const getUser = (): AdminUser | null => {
  const userStr = localStorage.getItem(USER_KEY);
  if (!userStr) return null;
  
  try {
    return JSON.parse(userStr);
  } catch {
    return null;
  }
};

/**
 * Remove user data from localStorage
 */
export const removeUser = (): void => {
  localStorage.removeItem(USER_KEY);
};

/**
 * Clear all authentication data
 */
export const clearAuth = (): void => {
  removeToken();
  removeUser();
};

/**
 * Check if user is authenticated
 */
export const isAuthenticated = (): boolean => {
  return !!getToken() && !!getUser();
};

/**
 * Check if user has admin privileges
 * Validates against both isAdmin flag and ADMIN_EMAILS environment variable
 */
export const isAdmin = (user: AdminUser | null): boolean => {
  if (!user) return false;
  
  const adminEmails = process.env.REACT_APP_ADMIN_EMAILS?.split(',').map(e => e.trim()) || [];
  return user.isAdmin || adminEmails.includes(user.email);
};

/**
 * Store complete authentication data (token + user)
 */
export const storeAuthData = (token: string, user: AdminUser): void => {
  setToken(token);
  setUser(user);
};
